import { useRef, useState } from 'react';
import { FileTextOutlined, RobotOutlined } from '@ant-design/icons';

import { Button, Field, Textarea } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { HanMucHoacCauGoc } from '../../../components/shared';
import { useT } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import type { LuotGoiY } from '../types';

export interface HopGoiYTieuChiProps {
  /** Lượt đang chạy hoặc đã xong; `undefined` = chưa bấm lần nào, hộp hiện form nhập. */
  luot: LuotGoiY | undefined;
  dangGui: boolean;
  loiGui: unknown;
  onBatDau: (moTa: string, tep?: File) => void;
  onApDung: () => void;
  onDong: () => void;
}

/**
 * AI gợi ý tiêu chí: nhập mô tả loại hồ sơ, theo dõi tiến độ, xem kết quả rồi quyết áp hay bỏ.
 *
 * Ba điều hộp này phải nói rõ, vì không nói thì người soạn hiểu sai thứ mình vừa nhận:
 *
 * 1. **Mô tả đi ra Internet** — lượt tra cứu gửi nó qua Google. Hộp nói ngay dưới ô nhập, trước khi gõ.
 * 2. **Tệp mẫu KHÔNG đi ra Internet** — nó chỉ vào lượt dựng tiêu chí. Không nói thì người soạn không dám
 *    gửi tệp, mà tệp mẫu là thứ làm gợi ý sát hồ sơ thật nhất.
 * 3. **Áp dụng là THAY cây tiêu chí đang soạn** — khác `can-trong-so` (chỉ đổi trọng số). Nút nói rõ số
 *    tiêu chí sẽ vào, và cảnh báo nằm trên nút.
 */
export function HopGoiYTieuChi({ luot, dangGui, loiGui, onBatDau, onApDung, onDong }: HopGoiYTieuChiProps) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const [moTa, setMoTa] = useState('');
  const [tep, setTep] = useState<File | undefined>();
  const oTep = useRef<HTMLInputElement>(null);

  const dangChay = luot ? ['cho', 'dang_chay'].includes(luot.trang_thai) : false;
  const kq = luot?.trang_thai === 'xong' ? luot.ket_qua : null;
  const soTieuChi = kq?.nhom.reduce((n, x) => n + x.tieu_chi.length, 0) ?? 0;

  return (
    <div className="space-y-3 border-b border-default bg-primary-50 p-3">
      <div className="flex items-start gap-2">
        <RobotOutlined className="mt-0.5 shrink-0 text-primary-600" />
        <p className="min-w-0 flex-1 font-medium text-primary-800">{t('thamDinh.soan.goiYTieuDe')}</p>
        <Button variant="ghost" size="sm" onClick={onDong}>{t('chung.dong')}</Button>
      </div>

      {!luot && (
        <div className="space-y-3">
          <Field label={t('thamDinh.soan.goiYMoTa')} required hint={t('thamDinh.soan.goiYMoTaMo')}>
            {(p) => (
              <Textarea
                {...p}
                rows={2}
                value={moTa}
                maxLength={1000}
                placeholder={t('thamDinh.soan.goiYMoTaVd')}
                onChange={(e) => setMoTa(e.target.value)}
              />
            )}
          </Field>

          <div className="space-y-1">
            <input
              ref={oTep}
              type="file"
              className="sr-only"
              accept=".pdf,.docx,.xlsx,.csv,.txt"
              onChange={(e) => setTep(e.target.files?.[0])}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" icon={<FileTextOutlined />} onClick={() => oTep.current?.click()}>
                {t('thamDinh.soan.goiYChonTep')}
              </Button>
              {tep && <span className="min-w-0 truncate text-sm text-content-muted">{tep.name}</span>}
              {tep && (
                <Button variant="ghost" size="sm" onClick={() => { setTep(undefined); if (oTep.current) oTep.current.value = ''; }}>
                  {t('chung.xoa')}
                </Button>
              )}
            </div>
            <p className="text-2xs text-content-muted">{t('thamDinh.soan.goiYTepMo')}</p>
          </div>

          {loiGui ? <p className="text-sm text-danger-700">{dichLoi(loiGui)}</p> : null}

          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              icon={<RobotOutlined />}
              loading={dangGui}
              disabled={moTa.trim().length === 0}
              onClick={() => onBatDau(moTa.trim(), tep)}
            >
              {t('thamDinh.soan.goiYBatDau')}
            </Button>
          </div>
        </div>
      )}

      {dangChay && (
        <div className="flex items-center gap-2 rounded border border-primary-200 bg-surface p-3">
          <span className="size-2 animate-pulse rounded-full bg-primary-500" aria-hidden="true" />
          <p className="min-w-0 flex-1 truncate text-sm text-primary-800">{luot!.buoc}</p>
        </div>
      )}

      {luot?.trang_thai === 'loi' && (
        <HanMucHoacCauGoc cau={luot.loi} bienThe="gon">
          <p className="rounded border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{luot.loi}</p>
        </HanMucHoacCauGoc>
      )}

      {kq && (
        <div className="space-y-3">
          <p className="text-sm text-content-muted">
            {t('thamDinh.soan.goiYKetQua', { so: soVN(soTieuChi, locale), model: kq.model })}
          </p>

          {kq.canh_bao.length > 0 && (
            <ul className="space-y-1 rounded border border-warning-200 bg-warning-50 p-2">
              {kq.canh_bao.map((c) => <li key={c} className="text-sm text-warning-800">{c}</li>)}
            </ul>
          )}

          {kq.tu_lieu.length > 0 && (
            <div className="rounded border border-default bg-surface p-2">
              <p className="text-2xs font-medium text-content-muted">{t('thamDinh.soan.goiYTuLieu')}</p>
              <ul className="mt-1 space-y-0.5">
                {kq.tu_lieu.map((tl, i) => (
                  <li key={tl.uri || tl.mien} className="truncate text-sm">
                    <span className="text-content-muted">[{soVN(i + 1, locale)}]</span> {tl.tieu_de}
                    <span className="text-2xs text-content-muted"> — {tl.mien}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {kq.nhom.length > 0 && (
            <ul className="max-h-60 space-y-2 overflow-y-auto rounded border border-default bg-surface p-2">
              {kq.nhom.map((n) => (
                <li key={n.ma}>
                  <p className="text-sm font-medium">{n.ten}</p>
                  <ul className="mt-0.5 space-y-0.5 pl-3">
                    {n.tieu_chi.map((tc) => (
                      <li key={tc.ma} className="truncate text-sm text-content-muted">
                        {tc.ten}
                        {tc.loai_tru && <span className="ml-1 text-2xs text-danger-700">{t('thamDinh.soan.loaiTru')}</span>}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}

          {soTieuChi > 0 && (
            <>
              <p className="text-sm text-warning-800">{t('thamDinh.soan.goiYThayCanh')}</p>
              <div className="flex justify-end">
                <Button variant="primary" size="sm" onClick={onApDung}>
                  {t('thamDinh.soan.goiYApDung', { so: soVN(soTieuChi, locale) })}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
